import {data, type ActionFunctionArgs} from '@remix-run/node'
import {useActionData, useLoaderData} from '@remix-run/react'
import {useState} from 'react'
import Markdown from 'react-markdown'

import {Page} from '#app/components/layout'
import {Button} from '#app/ui/button'
import {Form, getInputProps, useForm} from '#app/ui/forms/form'
import {AcceptRequestImage} from '#app/ui/images/accept-request'
import {StatusButton} from '#app/ui/status-button'
import {Textarea} from '#app/ui/textarea'
import {H1, Lead} from '#app/ui/typography'
import {db} from '#app/utils/db.server'
import {useIsPending} from '#app/utils/misc'
import {requireUserWithPermission} from '#app/utils/permissions.server'
import {redirectWithToast} from '#app/utils/toast.server'
import {useOptionalUser, userHasPermission} from '#app/utils/user'
import {getConstraint, parse, z} from '#app/utils/validation'

const RULES_MARKDOWN = 'rules-markdown'

export async function loader() {
  const rulesMarkdown = await db.textContent.findUnique({
    where: {name: RULES_MARKDOWN},
  })

  return {rulesMarkdown: rulesMarkdown?.content || ''}
}

export const action = async ({request}: ActionFunctionArgs) => {
  await requireUserWithPermission(request, 'update:textContent')

  const formData = await request.formData()
  const submission = parse(formData, {schema: RulesEditorSchema})

  if (submission.status !== 'success') {
    return data(submission.reply(), {status: 400})
  }

  await db.textContent.upsert({
    where: {name: RULES_MARKDOWN},
    create: {
      name: RULES_MARKDOWN,
      content: submission.value.rulesMarkdown,
    },
    update: {
      content: submission.value.rulesMarkdown,
    },
  })

  return redirectWithToast('/rules', {
    type: 'success',
    title: 'Success',
    description: 'Rules have been updated.',
  })
}

const RulesEditorSchema = z.object({
  rulesMarkdown: z.string(),
})

export default function Rules() {
  const data = useLoaderData<typeof loader>()
  const lastResult = useActionData<typeof action>()
  const isPending = useIsPending()
  const user = useOptionalUser()

  const canEdit = user && userHasPermission(user, 'update:textContent')

  const [isEditing, setIsEditing] = useState(false)
  const [previewText, setPreviewText] = useState(data.rulesMarkdown)

  const [form, fields] = useForm({
    constraint: getConstraint(RulesEditorSchema),
    lastResult,
    onValidate: ({formData}) => parse(formData, {schema: RulesEditorSchema}),
    defaultValue: {rulesMarkdown: data.rulesMarkdown},
  })

  return (
    <Page.Container>
      <div className="flex items-end justify-between">
        <div className="max-w-prose">
          <H1>Library Rules</H1>
          <Lead>
            Below you can find the rules of our library. Please read them carefully and if you have
            any questions, please contact the library administrator.
          </Lead>
        </div>
        {canEdit ? (
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button variant="outline" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
                <StatusButton
                  form={form.id}
                  type="submit"
                  disabled={isPending}
                  status={isPending ? 'pending' : 'idle'}
                >
                  Save
                </StatusButton>
              </>
            ) : (
              <Button onClick={() => setIsEditing(true)}>Edit</Button>
            )}
          </div>
        ) : null}
      </div>
      <div className="grid grid-cols-2 gap-8">
        <div className="prose dark:prose-invert">
          <Markdown>{previewText}</Markdown>
        </div>

        {/* If user can edit the rules (e.g. librarian), they won't see the image */}
        {/* but they've got the power to edit the rules, so don't complain! */}
        {!canEdit && <AcceptRequestImage />}

        {isEditing ? (
          <Form method="POST" form={form}>
            <Textarea
              {...getInputProps(fields.rulesMarkdown, {type: 'text'})}
              className="h-full p-4"
              onChange={e => setPreviewText(e.target.value)}
              placeholder="## Book Proposals ..."
            />
          </Form>
        ) : null}
      </div>
    </Page.Container>
  )
}
